/**
 * @license
 * Copyright Alibaba.com All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */

import { BACKSPACE, DOWN_ARROW, ENTER, ESCAPE, LEFT_ARROW, RIGHT_ARROW, UP_ARROW } from '@angular/cdk/keycodes';
import { CdkConnectedOverlay, ConnectedOverlayPositionChange, ConnectionPositionPair } from '@angular/cdk/overlay';
import {
	forwardRef,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	Host,
	HostListener,
	Input,
	OnDestroy,
	OnInit,
	Optional,
	Output,
	QueryList,
	Renderer2,
	TemplateRef,
	ViewChild,
	ViewChildren,
	ViewEncapsulation
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { animate, state, style, transition, trigger, AnimationTriggerMetadata } from '@angular/animations';
import { isDevMode } from '@angular/core';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { Subject } from 'rxjs';
import { startWith, takeUntil } from 'rxjs/operators';
import { InputBoolean } from 'ng-zorro-antd';

import {
	CascaderOption,
	CascaderSearchOption,
	CascaderComponentAsSource,
	CascaderExpandTrigger,
	CascaderSize,
	CascaderTriggerType,
	NzShowSearchOptions
} from './cascader-definitions';
import { CascaderOptionComponent } from './cascader-li.component';
import { CascaderService } from './cascader.service';
import { CascaderConfigService, WithConfig } from './cascader-config.service';
import { CascaderConfigKey } from './cascader.config';
import { FxI18nService } from '../i18n/nz-i18n.service';
import { FxCascaderI18nInterface } from '../i18n/nz-i18n.interface'
import { NzNoAnimationDirective } from '../../directive/nz-no-animation.directive';

class AnimationDuration {
	static SLOW = '0.3s'; // Modal
	static BASE = '0.2s';
	static FAST = '0.1s'; // Tooltip
}

class AnimationCurves {
	static EASE_BASE_OUT = 'cubic-bezier(0.7, 0.3, 0.1, 1)';
	static EASE_BASE_IN = 'cubic-bezier(0.9, 0, 0.3, 0.7)';
	static EASE_OUT = 'cubic-bezier(0.215, 0.61, 0.355, 1)';
	static EASE_IN = 'cubic-bezier(0.55, 0.055, 0.675, 0.19)';
	static EASE_IN_OUT = 'cubic-bezier(0.645, 0.045, 0.355, 1)';
	static EASE_OUT_BACK = 'cubic-bezier(0.12, 0.4, 0.29, 1.46)';
	static EASE_IN_BACK = 'cubic-bezier(0.71, -0.46, 0.88, 0.6)';
	static EASE_IN_OUT_BACK = 'cubic-bezier(0.71, -0.46, 0.29, 1.46)';
	static EASE_OUT_CIRC = 'cubic-bezier(0.08, 0.82, 0.17, 1)';
	static EASE_IN_CIRC = 'cubic-bezier(0.6, 0.04, 0.98, 0.34)';
	static EASE_IN_OUT_CIRC = 'cubic-bezier(0.78, 0.14, 0.15, 0.86)';
	static EASE_OUT_QUINT = 'cubic-bezier(0.23, 1, 0.32, 1)';
	static EASE_IN_QUINT = 'cubic-bezier(0.755, 0.05, 0.855, 0.06)';
	static EASE_IN_OUT_QUINT = 'cubic-bezier(0.86, 0, 0.07, 1)';
}

const ANIMATION_TRANSITION_IN = `0.2s cubic-bezier(0.23, 1, 0.32, 1)`;
const ANIMATION_TRANSITION_OUT = `0.2s cubic-bezier(0.755, 0.05, 0.855, 0.06)`;
// const ANIMATION_TRANSITION_IN = `${AnimationDuration.BASE} ${AnimationCurves.EASE_OUT_QUINT}`;
// const ANIMATION_TRANSITION_OUT = `${AnimationDuration.BASE} ${AnimationCurves.EASE_IN_QUINT}`;
const PREFIX = '[NG-ZORRO]:';
const environment = {
	isTestMode: false
};
const slideMotion: AnimationTriggerMetadata = trigger('slideMotion', [
	state(
		'bottom',
		style({
			opacity: 1,
			transform: 'scaleY(1)',
			transformOrigin: '0% 0%'
		})
	),
	state(
		'top',
		style({
			opacity: 1,
			transform: 'scaleY(1)',
			transformOrigin: '0% 100%'
		})
	),
	transition('void => bottom', [
		style({
			opacity: 0,
			transform: 'scaleY(0.8)',
			transformOrigin: '0% 0%'
		}),
		animate(ANIMATION_TRANSITION_IN)
	]),
	transition('bottom => void', [
		animate(
			ANIMATION_TRANSITION_OUT,
			style({
				opacity: 0,
				transform: 'scaleY(0.8)',
				transformOrigin: '0% 0%'
			})
		)
	]),
	transition('void => top', [
		style({
			opacity: 0,
			transform: 'scaleY(0.8)',
			transformOrigin: '0% 100%'
		}),
		animate(ANIMATION_TRANSITION_IN)
	]),
	transition('top => void', [
		animate(
			ANIMATION_TRANSITION_OUT,
			style({
				opacity: 0,
				transform: 'scaleY(0.8)',
				transformOrigin: '0% 100%'
			})
		)
	])
]);
const POSITION_MAP: { [key: string]: ConnectionPositionPair } = {
	top: new ConnectionPositionPair(
		{ originX: 'center', originY: 'top' },
		{
			overlayX: 'center',
			overlayY: 'bottom'
		}
	),
	topCenter: new ConnectionPositionPair(
		{ originX: 'center', originY: 'top' },
		{ overlayX: 'center', overlayY: 'bottom' }
	),
	topLeft: new ConnectionPositionPair(
		{ originX: 'start', originY: 'top' },
		{
			overlayX: 'start',
			overlayY: 'bottom'
		}
	),
	topRight: new ConnectionPositionPair({ originX: 'end', originY: 'top' }, { overlayX: 'end', overlayY: 'bottom' }),
	right: new ConnectionPositionPair(
		{ originX: 'end', originY: 'center' },
		{
			overlayX: 'start',
			overlayY: 'center'
		}
	),
	rightTop: new ConnectionPositionPair({ originX: 'end', originY: 'top' }, { overlayX: 'start', overlayY: 'top' }),
	rightBottom: new ConnectionPositionPair(
		{ originX: 'end', originY: 'bottom' },
		{ overlayX: 'start', overlayY: 'bottom' }
	),
	bottom: new ConnectionPositionPair(
		{ originX: 'center', originY: 'bottom' },
		{
			overlayX: 'center',
			overlayY: 'top'
		}
	),
	bottomCenter: new ConnectionPositionPair(
		{ originX: 'center', originY: 'bottom' },
		{ overlayX: 'center', overlayY: 'top' }
	),
	bottomLeft: new ConnectionPositionPair(
		{ originX: 'start', originY: 'bottom' },
		{ overlayX: 'start', overlayY: 'top' }
	),
	bottomRight: new ConnectionPositionPair({ originX: 'end', originY: 'bottom' }, { overlayX: 'end', overlayY: 'top' }),
	left: new ConnectionPositionPair(
		{ originX: 'start', originY: 'center' },
		{
			overlayX: 'end',
			overlayY: 'center'
		}
	),
	leftTop: new ConnectionPositionPair({ originX: 'start', originY: 'top' }, { overlayX: 'end', overlayY: 'top' }),
	leftBottom: new ConnectionPositionPair(
		{ originX: 'start', originY: 'bottom' },
		{ overlayX: 'end', overlayY: 'bottom' }
	)
};
const DEFAULT_DROPDOWN_POSITIONS = [
	POSITION_MAP.bottomLeft,
	POSITION_MAP.bottomRight,
	POSITION_MAP.topRight,
	POSITION_MAP.topLeft
];
const lowercaseFirstLetter = (s: string): string => {
	return s.charAt(0).toLowerCase() + s.slice(1);
};
const trimComponentName = (componentName: string): CascaderConfigKey => {
	return lowercaseFirstLetter(
		componentName
			.replace('Cascader', '')
			.replace(/(Component|Directive|Service|ContainerComponent)$/g, '')
			.toLowerCase()
	) as CascaderConfigKey;
};

const warnDeprecation = (...args: any[]) => {
	const stack = new Error().stack;
	return consoleCommonBehavior((...arg: any[]) => console.warn(PREFIX, 'deprecated:', ...arg, stack), ...args);
};
const record: Record<string, boolean> = {};

function consoleCommonBehavior(consoleFunc: (...args: any) => void, ...args: any[]): void {
	if (environment.isTestMode || (isDevMode() && notRecorded(...args))) {
		consoleFunc(...args);
	}
}
function notRecorded(...args: any[]): boolean {
	const asRecord = args.reduce((acc, c) => acc + c.toString(), '');

	if (record[asRecord]) {
		return false;
	} else {
		record[asRecord] = true;
		return true;
	}
}

function toBoolean(value: boolean | string): boolean {
	return coerceBooleanProperty(value);
}

type CascaderClassType = string | string[] | Set<string> | CascaderClassInterface;

interface CascaderClassInterface {
	[klass: string]: any; // tslint:disable-line:no-any
}

interface CascaderStyleInterface {
	[klass: string]: any; // tslint:disable-line:no-any
}


// export const cascaderHelper = {
//     slideMotion: slideMotion,
//     trimComponentName: trimComponentName,
//     warnDeprecation: warnDeprecation,
//     DEFAULT_DROPDOWN_POSITIONS: DEFAULT_DROPDOWN_POSITIONS
// };
const defaultDisplayRender = (labels: string[]) => labels.join(' / ');

// @dynamic
@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
	selector: 'zx-cascader, [zx-cascader]',
	exportAs: 'Cascader',
	preserveWhitespaces: false,
	templateUrl: './cascader.component.html',
	animations: [slideMotion],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => CascaderComponent),
			multi: true
		},
		CascaderService
	],
	host: {
		'[attr.tabIndex]': '"0"',
		'[class.ant-cascader-lg]': 'nzSize === "large"',
		'[class.ant-cascader-sm]': 'nzSize === "small"',
		'[class.ant-cascader-picker-disabled]': 'nzDisabled',
		'[class.ant-cascader-picker-open]': 'menuVisible',
		'[class.ant-cascader-picker-with-value]': '!!inputValue',
		'[class.ant-cascader-picker-with-labelRenderText]': '!!labelRenderText',
		'[class.ant-cascader-focused]': 'isFocused'
	},
	styles: [
		`
      .ant-cascader-menus {
        margin-top: 4px;
        margin-bottom: 4px;
        top: 100%;
        left: 0;
        position: relative;
        width: 100%;
      }
      .ant-cascader-picker-with-labelRenderText .ant-cascader-picker-label{
        color: rgba(0, 0, 0, 0.65);
      }
      .ant-cascader-picker-with-labelRenderText input{
        color: transparent;
      }
    `
	]
})
export class CascaderComponent implements CascaderComponentAsSource, OnInit, OnDestroy, ControlValueAccessor {
	@ViewChild('input', {static: true}) input: ElementRef;
	@ViewChild('menu', {static: true}) menu: ElementRef;
	@ViewChild(CdkConnectedOverlay, {static: true}) overlay: CdkConnectedOverlay;
	@ViewChildren(CascaderOptionComponent) cascaderItems: QueryList<CascaderOptionComponent>;

	@Input() @InputBoolean() nzShowInput = true;
	@Input() @InputBoolean() nzShowArrow = true;
	@Input() @InputBoolean() nzAllowClear = true;
	@Input() @InputBoolean() nzAutoFocus = false;
	@Input() @InputBoolean() nzChangeOnSelect = false;
	@Input() @InputBoolean() nzDisabled = false;
	@Input() nzColumnClassName: string;
	@Input() nzExpandTrigger: CascaderExpandTrigger = 'click';
	@Input() nzValueProperty = 'value';
	@Input() nzLabelRender: TemplateRef<void>;
	@Input() nzLabelProperty = 'label';
	@Input() nzNotFoundContent: string | TemplateRef<void>;
	@Input() @WithConfig('default') nzSize: CascaderSize;
	@Input() nzShowSearch: boolean | NzShowSearchOptions;
	@Input() nzPlaceHolder: string;
	@Input() nzMenuClassName: string;
	@Input() nzMenuStyle: { [key: string]: string };
	@Input() nzMouseEnterDelay: number = 150; // ms
	@Input() nzMouseLeaveDelay: number = 150; // ms
	@Input() nzTriggerAction: CascaderTriggerType | CascaderTriggerType[] = ['click'] as CascaderTriggerType[];
	@Input() nzChangeOn: (option: CascaderOption, level: number) => boolean;
	@Input() nzLoadData: (node: CascaderOption, index?: number) => PromiseLike<any>; // tslint:disable-line:no-any

	@Input()
	get nzOptions(): CascaderOption[] | null {
		return this.cascaderService.nzOptions;
	}

	set nzOptions(options: CascaderOption[] | null) {
		this.cascaderService.withOptions(options);
	}

	@Output() readonly nzVisibleChange = new EventEmitter<boolean>();

	@Output() readonly nzSelectionChange = new EventEmitter<CascaderOption[]>();

	/**
	 * @deprecated 9.0.0. This api is a duplication of `ngModelChange`.
	 */
	@Output() readonly nzSelect = new EventEmitter<{ option: CascaderOption; index: number } | null>();

	@Output() readonly nzClear = new EventEmitter<void>();

	el: HTMLElement;
	dropDownPosition = 'bottom';
	menuVisible = false;
	isLoading = false;
	labelRenderText: string;
	labelRenderContext = {};
	onChange = Function.prototype;
	onTouched = Function.prototype;
	reLoadData = Function.prototype;
	positions: ConnectionPositionPair[] = [...DEFAULT_DROPDOWN_POSITIONS];
	dropdownWidthStyle: string;
	isFocused = false;

	locale: FxCascaderI18nInterface;

	private delayingLoad;
	private $destroy = new Subject<void>();
	private inputString = '';
	private isOpening = false;
	private delayMenuTimer: any | null;
	private delaySelectTimer: any | null;

	get inSearchingMode(): boolean {
		return this.cascaderService.inSearchingMode;
	}

	set inputValue(inputValue: string) {
		this.inputString = inputValue;
		this.toggleSearchingMode(!!inputValue);
	}

	get inputValue(): string {
		return this.inputString;
	}

	get menuCls(): CascaderClassType {
		return { [`${this.nzMenuClassName}`]: !!this.nzMenuClassName };
	}

	get menuColumnCls(): CascaderClassType {
		return { [`${this.nzColumnClassName}`]: !!this.nzColumnClassName };
	}

	private get hasInput(): boolean {
		return !!this.inputValue;
	}

	private get hasValue(): boolean {
		return this.cascaderService.values && this.cascaderService.values.length > 0;
	}

	get showPlaceholder(): boolean {
		return !(this.hasInput || this.hasValue);
	}

	get clearIconVisible(): boolean {
		return this.nzAllowClear && !this.nzDisabled && (this.hasValue || this.hasInput);
	}

	get isLabelRenderTemplate(): boolean {
		return !!this.nzLabelRender;
	}

	constructor(
		public cascaderService: CascaderService,
		private i18nService: FxI18nService,
		public nzConfigService: CascaderConfigService,
		private cdr: ChangeDetectorRef,
		elementRef: ElementRef,
		renderer: Renderer2,
		@Host() @Optional() public noAnimation?: NzNoAnimationDirective
	) {
		this.el = elementRef.nativeElement;
		this.cascaderService.withComponent(this);
		renderer.addClass(elementRef.nativeElement, 'ant-cascader');
		renderer.addClass(elementRef.nativeElement, 'ant-cascader-picker');
	}

	ngOnInit(): void {
		const srv = this.cascaderService;

		srv.$redraw.pipe(takeUntil(this.$destroy)).subscribe(() => {
			// These operations would not mutate data.
			this.checkChildren();
			this.buildDisplayLabel();
			this.reposition();
			this.cdr.markForCheck();
		});

		srv.$loading.pipe(takeUntil(this.$destroy)).subscribe(loading => {
			this.isLoading = loading;
		});

		srv.$optionSelected.pipe(takeUntil(this.$destroy)).subscribe(data => {
			if (!data) {
				this.onChange([]);
				this.nzSelect.emit(null);
				this.nzSelectionChange.emit([]);
			} else {
				const { option, index } = data;
				const shouldClose = option.isLeaf;
				if (shouldClose) {
					this.delaySetMenuVisible(false);
				}
				this.onChange(this.cascaderService.values);
				this.nzSelectionChange.emit(this.cascaderService.selectedOptions);
				this.nzSelect.emit({ option, index });
				this.cdr.markForCheck();
			}
		});

		srv.$quitSearching.pipe(takeUntil(this.$destroy)).subscribe(() => {
			this.inputString = '';
			this.dropdownWidthStyle = '';
		});

		this.i18nService.localeChange
			.pipe(
				startWith(),
				takeUntil(this.$destroy)
			)
			.subscribe(() => {
				this.setLocale();
			});

		this.nzConfigService
			.getConfigChangeEventForComponent(trimComponentName(this.constructor.name))
			.pipe(takeUntil(this.$destroy))
			.subscribe(() => {
				this.cdr.markForCheck();
			});

		if (this.nzSelect.observers.length > 0) {
			warnDeprecation(`nzSelect is deprecated and will be removed in 9.0.0. Please use 'nzSelectionChange' instead.`);
		}
		this.reLoadData = () => {
			this.cascaderService.columns = [];
			this.cascaderService.loadChildren({}, -1);
		};
	}

	ngOnDestroy(): void {
		this.$destroy.next();
		this.$destroy.complete();
		this.clearDelayMenuTimer();
		this.clearDelaySelectTimer();
	}

	registerOnChange(fn: () => {}): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => {}): void {
		this.onTouched = fn;
	}

	// tslint:disable-next-line:no-any
	toArray<T>(value: T | T[]): T[] {
		let ret: T[];
		if (value == null) {
			ret = [];
		} else if (!Array.isArray(value)) {
			ret = [value];
		} else {
			ret = value;
		}
		return ret;
	}
	writeValue(value: any): void {
		this.cascaderService.values = this.toArray(value);
		this.cascaderService.syncOptions(true);
	}

	delaySetMenuVisible(visible: boolean, delay: number = 100, setOpening: boolean = false): void {
		this.clearDelayMenuTimer();
		if (delay) {
			if (visible && setOpening) {
				this.isOpening = true;
			}
			this.delayMenuTimer = setTimeout(() => {
				this.setMenuVisible(visible);
				this.cdr.detectChanges();
				this.clearDelayMenuTimer();
				if (visible) {
					setTimeout(() => {
						this.isOpening = false;
					}, 100);
				}
			}, delay);
		} else {
			this.setMenuVisible(visible);
		}
	}

	setMenuVisible(visible: boolean): void {
		if (this.nzDisabled || this.menuVisible === visible) {
			return;
		}
		if (visible) {
			this.cascaderService.syncOptions();
		}

		this.menuVisible = visible;
		this.nzVisibleChange.emit(visible);
		this.cdr.detectChanges();
	}

	private clearDelayMenuTimer(): void {
		if (this.delayMenuTimer) {
			clearTimeout(this.delayMenuTimer);
			this.delayMenuTimer = null;
		}
	}

	clearSelection(event?: Event): void {
		if (event) {
			event.preventDefault();
			event.stopPropagation();
		}

		this.cascaderService.values = [];
		this.labelRenderText = '';
		this.labelRenderContext = {};
		this.inputValue = '';
		this.setMenuVisible(false);
		this.cascaderService.clear();
	}

	// tslint:disable-next-line:no-any
	getSubmitValue(): any[] {
		return this.cascaderService.selectedOptions.map(o => this.cascaderService.getOptionValue(o));
	}

	focus(): void {
		if (!this.isFocused) {
			(this.input ? this.input.nativeElement : this.el).focus();
			this.isFocused = true;
		}
	}

	blur(): void {
		if (this.isFocused) {
			(this.input ? this.input.nativeElement : this.el).blur();
			this.isFocused = false;
		}
	}

	handleInputBlur(): void {
		this.menuVisible ? this.focus() : this.blur();
	}

	handleInputFocus(): void {
		this.focus();
	}

	@HostListener('keydown', ['$event'])
	onKeyDown(event: KeyboardEvent): void {
		const keyCode = event.keyCode;

		if (
			keyCode !== DOWN_ARROW &&
			keyCode !== UP_ARROW &&
			keyCode !== LEFT_ARROW &&
			keyCode !== RIGHT_ARROW &&
			keyCode !== ENTER &&
			keyCode !== BACKSPACE &&
			keyCode !== ESCAPE
		) {
			return;
		}

		// Press any keys above to reopen menu.
		if (!this.menuVisible && keyCode !== BACKSPACE && keyCode !== ESCAPE) {
			return this.setMenuVisible(true);
		}

		// Make these keys work as default in searching mode.
		if (this.inSearchingMode && (keyCode === BACKSPACE || keyCode === LEFT_ARROW || keyCode === RIGHT_ARROW)) {
			return;
		}

		// Interact with the component.
		if (this.menuVisible) {
			event.preventDefault();
			if (keyCode === DOWN_ARROW) {
				this.moveUpOrDown(false);
			} else if (keyCode === UP_ARROW) {
				this.moveUpOrDown(true);
			} else if (keyCode === LEFT_ARROW) {
				this.moveLeft();
			} else if (keyCode === RIGHT_ARROW) {
				this.moveRight();
			} else if (keyCode === ENTER) {
				this.onEnter();
			}
		}
	}

	@HostListener('click')
	onTriggerClick(): void {
		if (this.nzDisabled) {
			return;
		}
		if (this.nzShowSearch) {
			this.focus();
		}
		if (this.isActionTrigger('click')) {
			this.delaySetMenuVisible(!this.menuVisible, 100);
		}
		this.onTouched();
	}

	@HostListener('mouseenter')
	onTriggerMouseEnter(): void {
		if (this.nzDisabled || !this.isActionTrigger('hover')) {
			return;
		}

		this.delaySetMenuVisible(true, this.nzMouseEnterDelay, true);
	}

	@HostListener('mouseleave', ['$event'])
	onTriggerMouseLeave(event: MouseEvent): void {
		if (this.nzDisabled || !this.menuVisible || this.isOpening || !this.isActionTrigger('hover')) {
			event.preventDefault();
			return;
		}
		const mouseTarget = event.relatedTarget as HTMLElement;
		const hostEl = this.el;
		const menuEl = this.menu && (this.menu.nativeElement as HTMLElement);
		if (hostEl.contains(mouseTarget) || (menuEl && menuEl.contains(mouseTarget))) {
			return;
		}
		this.delaySetMenuVisible(false, this.nzMouseLeaveDelay);
	}

	onOptionMouseEnter(option: CascaderOption, columnIndex: number, event: Event): void {
		event.preventDefault();
		if (this.nzExpandTrigger === 'hover') {
			if (!option.isLeaf) {
				this.delaySetOptionActivated(option, columnIndex, false);
			} else {
				this.cascaderService.setOptionDeactivatedSinceColumn(columnIndex);
			}
		}
	}

	onOptionMouseLeave(option: CascaderOption, _columnIndex: number, event: Event): void {
		event.preventDefault();
		if (this.nzExpandTrigger === 'hover' && !option.isLeaf) {
			this.clearDelaySelectTimer();
		}
	}

	onOptionClick(option: CascaderOption, columnIndex: number, event: Event): void {
		if (event) {
			event.preventDefault();
		}
		if (option && option.disabled) {
			return;
		}
		this.el.focus();
		!!this.nzShowSearch['query'] || !this.inSearchingMode
			? this.cascaderService.setOptionActivated(option, columnIndex, true)
			: this.cascaderService.setSearchOptionSelected(option as CascaderSearchOption);
	}

	private isActionTrigger(action: 'click' | 'hover'): boolean {
		return typeof this.nzTriggerAction === 'string'
			? this.nzTriggerAction === action
			: this.nzTriggerAction.indexOf(action) !== -1;
	}

	private onEnter(): void {
		const columnIndex = Math.max(this.cascaderService.activatedOptions.length - 1, 0);
		const option = this.cascaderService.activatedOptions[columnIndex];
		if (option && !option.disabled) {
			!!this.nzShowSearch['query'] || !this.inSearchingMode
				? this.cascaderService.setOptionActivated(option, columnIndex, true)
				: this.cascaderService.setSearchOptionSelected(option as CascaderSearchOption);
		}
	}

	private moveUpOrDown(isUp: boolean): void {
		const columnIndex = Math.max(this.cascaderService.activatedOptions.length - 1, 0);
		const activeOption = this.cascaderService.activatedOptions[columnIndex];
		const options = this.cascaderService.columns[columnIndex] || [];
		const length = options.length;
		let nextIndex = -1;
		if (!activeOption) {
			// Not selected options in this column
			nextIndex = isUp ? length : -1;
		} else {
			nextIndex = options.indexOf(activeOption);
		}

		while (true) {
			nextIndex = isUp ? nextIndex - 1 : nextIndex + 1;
			if (nextIndex < 0 || nextIndex >= length) {
				break;
			}
			const nextOption = options[nextIndex];
			if (!nextOption || nextOption.disabled) {
				continue;
			}
			this.cascaderService.setOptionActivated(nextOption, columnIndex);
			break;
		}
	}

	private moveLeft(): void {
		const options = this.cascaderService.activatedOptions;
		if (options.length) {
			options.pop(); // Remove the last one
		}
	}

	private moveRight(): void {
		const length = this.cascaderService.activatedOptions.length;
		const options = this.cascaderService.columns[length];
		if (options && options.length) {
			const nextOpt = options.find(o => !o.disabled);
			if (nextOpt) {
				this.cascaderService.setOptionActivated(nextOpt, length);
			}
		}
	}

	private clearDelaySelectTimer(): void {
		if (this.delaySelectTimer) {
			clearTimeout(this.delaySelectTimer);
			this.delaySelectTimer = null;
		}
	}

	private delaySetOptionActivated(option: CascaderOption, columnIndex: number, performSelect: boolean): void {
		this.clearDelaySelectTimer();
		this.delaySelectTimer = setTimeout(() => {
			this.cascaderService.setOptionActivated(option, columnIndex, performSelect);
			this.delaySelectTimer = null;
		}, 150);
	}

	private toggleSearchingMode(toSearching: boolean): void {
		if (this.inSearchingMode !== toSearching) {
			this.cascaderService.toggleSearchingMode(toSearching);
			this.dropdownWidthStyle = toSearching ? `${this.input.nativeElement.offsetWidth}px` : '';
		}
		if (this.inSearchingMode) {
			if (this.delayingLoad) clearTimeout(this.delayingLoad);
			this.delayingLoad = setTimeout(() => {
				this.delayingLoad = null;
				this.cascaderService.prepareSearchOptions(this.inputValue);
			}, 300);
		}
	}

	isOptionActivated(option: CascaderOption, index: number): boolean {
		const activeOpt = this.cascaderService.activatedOptions[index];
		return activeOpt === option;
	}

	setDisabledState(isDisabled: boolean): void {
		if (isDisabled) {
			this.closeMenu();
		}
		this.nzDisabled = isDisabled;
	}

	closeMenu(): void {
		this.blur();
		this.clearDelayMenuTimer();
		this.setMenuVisible(false);
	}

	onPositionChange(position: ConnectedOverlayPositionChange): void {
		const newValue = position.connectionPair.originY === 'bottom' ? 'bottom' : 'top';
		if (this.dropDownPosition !== newValue) {
			this.dropDownPosition = newValue;
			this.cdr.detectChanges();
		}
	}

	/**
	 * Reposition the cascader panel. When a menu opens, the cascader expands
	 * and may exceed the boundary of browser's window.
	 */
	private reposition(): void {
		if (this.overlay && this.overlay.overlayRef && this.menuVisible) {
			Promise.resolve().then(() => {
				this.overlay.overlayRef.updatePosition();
			});
		}
	}

	/**
	 * When a cascader options is changed, a child needs to know that it should re-render.
	 */
	private checkChildren(): void {
		if (this.cascaderItems) {
			this.cascaderItems.forEach(item => item.markForCheck());
		}
	}

	private buildDisplayLabel(): void {
		const selectedOptions = this.cascaderService.selectedOptions;
		const labels: string[] = selectedOptions.map(o => this.cascaderService.getOptionLabel(o));

		if (this.isLabelRenderTemplate) {
			this.labelRenderContext = { labels, selectedOptions };
		}
		this.labelRenderText = defaultDisplayRender.call(this, labels, selectedOptions);
	}

	private setLocale(): void {
		this.locale = this.i18nService.getLocaleData('global');
		this.cdr.markForCheck();
	}

}
